import { z } from 'zod'

// The member banner: one announcement an administrator can pin under the
// header bar of every page of the member app. Off by default -- a missing
// setting is "no banner" -- and stored as three rows in intra.settings, so
// switching it on needs no migration.

export const BANNER_SETTINGS = {
  enabled: 'banner.enabled',
  severity: 'banner.severity',
  message: 'banner.message',
} as const

// Same vocabulary as MUI's Alert, which renders it.
export const BANNER_SEVERITIES = ['info', 'success', 'warning', 'error'] as const
export type BannerSeverity = (typeof BANNER_SEVERITIES)[number]

export const DEFAULT_BANNER_SEVERITY: BannerSeverity = 'info'
export const BANNER_MESSAGE_MAX = 500

export const BannerSeveritySchema = z.enum(BANNER_SEVERITIES)

// What an administrator saves. Plain text only: the client renders it as a
// text node, never as HTML.
export const BannerInputSchema = z
  .object({
    enabled: z.boolean(),
    severity: BannerSeveritySchema,
    message: z.string().trim().max(BANNER_MESSAGE_MAX),
  })
  .refine((b) => !b.enabled || b.message !== '', {
    message: 'A banner that is switched on needs a message',
    path: ['message'],
  })

export type BannerInput = z.infer<typeof BannerInputSchema>

// The admin's view of the banner, even while it is off.
export type BannerSettings = BannerInput

// What members see: null while the banner is off or has nothing to say.
export interface MemberBanner {
  severity: BannerSeverity
  message: string
}

// Reads the banner out of raw setting values. Never throws: an unreadable
// row means "off" / the default severity, not a broken page.
export function readBannerSettings(get: (key: string) => string | null | undefined): BannerSettings {
  const severity = BannerSeveritySchema.safeParse(get(BANNER_SETTINGS.severity))
  return {
    enabled: get(BANNER_SETTINGS.enabled) === 'true',
    severity: severity.success ? severity.data : DEFAULT_BANNER_SEVERITY,
    message: (get(BANNER_SETTINGS.message) ?? '').trim(),
  }
}

export function toMemberBanner(banner: BannerSettings): MemberBanner | null {
  return banner.enabled && banner.message !== '' ? { severity: banner.severity, message: banner.message } : null
}
